import { selectLanguage, setAvailableLanguages, useAppDispatch } from "@org-avp/avp-avengers-ui-shared-store";
import { Translations } from "@org-avp/avp-avengers-ui-shared-utils";
import { createInstance } from "i18next";
import { FC, PropsWithChildren } from "react";
import { initReactI18next } from "react-i18next";
import { useSelector } from "react-redux";

export interface I18nProps {
  specifiedTranslations: Translations[];
}

export const I18n: FC<PropsWithChildren<I18nProps>> = (props) => {
  // get dispatch and state - has to be done unconditionally (outside of if-clause)
  const dispatch = useAppDispatch();
  const languageState = useSelector(selectLanguage);

  // prevent duplicate initialization
  if (languageState.availableLanguages.length < 1) {
    // merge initial translations an all translations from props
    const translations = mergeTranslations(props.specifiedTranslations);
    console.log("INFO: i18n translations", Object.keys(translations));

    // extract namespaces from translations
    const ns = Object.values(translations).flatMap(Object.keys).reduce<string[]>((acc, next) => acc.includes(next) ? acc : [...acc, next], []);

    createInstance()
      // pass the i18n instance to react-i18next.
      .use(initReactI18next)
      // init i18next
      // for all options read: https://www.i18next.com/overview/configuration-options
      .init({
        debug: false,
        lng: languageState.selectedLanguage,
        fallbackLng: "en",
        interpolation: {
          escapeValue: false, // not needed for react as it escapes by default
        },
        ns,
        resources: translations,
      })
      .then(() => console.log(`INFO: i18n initialized, language is set to ${languageState.selectedLanguage}`));

    // extract available languages from translations and initialize them in store entry
    dispatch(setAvailableLanguages(Object.keys(translations)));
  }

  return props.children;
};

function mergeTranslations(translations: Translations[]): Translations {
  return translations.reduce(
    (tAcc, tNnext) => {
      const tResult: Translations = {};
      const languageKeys = new Set<string>([...Object.keys(tAcc), ...Object.keys(tNnext)]);
      languageKeys.forEach((lng) => tResult[lng] = [tAcc[lng], tNnext[lng]].reduce(
        (nsAcc, nsNext) => {
          const nsResult: Translations[string] = {};
          const namespaces = new Set<string>([...Object.keys(nsAcc), ...(nsNext ? Object.keys(nsNext) : [])]);
          namespaces.forEach((ns) => nsResult[ns] = { ...nsAcc[ns], ...nsNext[ns] });
          return nsResult;
        },
        {},
      ));
      return tResult;
    },
    {},
  );
}
